import { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'

import { MediaOrderFormContext } from '../../../../../MediaOrderFormContext'

import { formatDateToBE } from '../../../../../../../../constants/dates'
import { activeContractSelector } from '../../../../../../../../modules/selectors/contracts'
import { selectedControllerDataSelector } from '../../../../../../../../modules/selectors/app'

import { getInventoryService } from '../../../../../../../../modules/services/mediaOrdersProducts'
import { handlePeriodsSelection, initialProductPeriodsValues } from '../AddProductBulkContent/handlePeriodSelection'
import { MEDIA_PRODUCTS } from '../../../../fields'

const useProductsInventory = setFieldValue => {
  const selectedController = useSelector(selectedControllerDataSelector)
  const activeContract = useSelector(activeContractSelector)
  const contractId = activeContract?.id
  const supportCustomDates = selectedController?.custom_booking_dates

  const {
    // contextSelfAccountData,
    currency
  } = useContext(MediaOrderFormContext)
  const currencyCode = currency?.code

  const fetchProductsInventory = useCallback(
    (productsIdsList, range) => {
      const inventoryParams = {
        media_product: productsIdsList.join(','),
        currency: currencyCode,
        competing_quotation: true, // allows to get competing quotation data
        // allows to avoid competing inventory to itself
        exclude_competing_contract: contractId,
        // passing product is product ID from media order, this will ask BE to respond with additional
        // product_booked_quantity field which is equivalent to the quantity of booked periods within the media order
        // ...(mediaOrderProductId && { product: mediaOrderProductId })
        available_to_book: true,
        // for custom dates the periods could be selected few days before or after periods based on before_percentage
        ...(supportCustomDates
          ? {
              date_end_after: formatDateToBE(range.startDate),
              date_start_before: formatDateToBE(range.endDate)
            }
          : // for not custom dates the periods could be selected only within the selected date range
            {
              date_start_after: formatDateToBE(range.startDate),
              date_end_before: formatDateToBE(range.endDate)
            }),
        group: true
      }

      return getInventoryService(inventoryParams)
    },
    [contractId, currencyCode, supportCustomDates]
  )

  const fetchAndSelectInventoryPeriods = useCallback(
    async (products, dateRange, startIndex = 0) => {
      const productsList = products.map(product => product.data.id)
      // don't fetch inventory if there are no selected products or date range is not set
      if (!!productsList.length && !!dateRange?.startDate && !!dateRange.endDate) {
        const inventoryData = await fetchProductsInventory(productsList, dateRange)

        handlePeriodsSelection({
          products,
          productsInventory: inventoryData,
          dateRange,
          selectedController,
          setFieldValue,
          startIndex
        })
      }
    },
    [fetchProductsInventory, selectedController, setFieldValue]
  )

  const handleReselectPeriods = useCallback(
    (products, dateRange) => {
      products.forEach((product, index) => {
        setFieldValue(`${MEDIA_PRODUCTS}.${index}`, {
          ...product,
          ...initialProductPeriodsValues,
          isInventoryLoading: true
        })
      })

      fetchAndSelectInventoryPeriods(products, dateRange)
    },
    [fetchAndSelectInventoryPeriods, setFieldValue]
  )

  return {
    fetchAndSelectInventoryPeriods,
    handleReselectPeriods
  }
}

export default useProductsInventory
